import type { DuoMeasurementValues, View } from "@novorender/api";
import { useEffect } from "react";

import { useAppDispatch } from "app/redux-store-interactions";
import { telemetry } from "telemetry";

import { type PointLine, pointLineActions } from "../pointLineSlice";

/**
 * For each point line that has exactly 2 points: measure per axis distance.
 */
export function useMeasurePointToPointAxes(view: View | undefined, pointLines: PointLine[]) {
    const dispatch = useAppDispatch();

    useEffect(() => {
        const abortController = new AbortController();
        measure();

        async function measure() {
            const measureView = view?.measure;
            if (!measureView) {
                return;
            }

            const promises = pointLines.map(async (pointLine) => {
                const {
                    points: [p1, p2],
                } = pointLine;

                if (pointLine.points.length !== 2 || !p1.objectId || !p2.objectId) {
                    return;
                }

                if (pointLine.firstTwoPointAxisMeasurements) {
                    return pointLine.firstTwoPointAxisMeasurements;
                }

                return (await measureView.core
                    .measure(
                        { ObjectId: p1.objectId, drawKind: "vertex", parameter: p1.position },
                        { ObjectId: p2.objectId, drawKind: "vertex", parameter: p2.position },
                    )
                    .catch((e) => {
                        console.warn(e);
                        telemetry.trackCaughtException({
                            source: "pointLine.useMeasurePointToPointAxes.measure",
                            category: "render",
                            error: e,
                        });
                    })) as DuoMeasurementValues | undefined;
            });

            const results = await Promise.all(promises);
            if (abortController.signal.aborted) {
                return;
            }

            dispatch(pointLineActions.setFirstTwoPointAxisMeasurements(results));
        }

        return () => {
            abortController.abort();
        };
    }, [dispatch, view, pointLines]);
}
